import { CLAIM_TYPE_PROVIDER_CONFIG } from './providerConfig';

/** Invariant: `required` implies `visible` — a hidden field is never required. */
export interface FormFieldSetting {
  visible: boolean;
  required: boolean;
}

export interface EncounterFieldsConfig {
  encounterFields: Record<string, FormFieldSetting>;
  claimTypeFields: Record<string, Record<string, FormFieldSetting>>;
}

/**
 * Structural intake fields rendered outside the patient/encounter sections — documents
 * upload, bill-type selection, provider assignment. Fully configurable per practice
 * (both visibility and required, invariant enforced), exactly like the patient/encounter
 * fields; they're a separate list only because the portal renders them via different
 * components (FileUploadZone, ClaimTypeSection, ClaimBillTypeCard's Providers subblock).
 * The settings save keeps at least one identity-anchor group fully required (see
 * {@link IDENTITY_ANCHOR_GROUPS}, {@link hasRequiredIdentityAnchor}); before the
 * 2026-07-16 amendment `documents` was a hard lock (always visible + required).
 */
export const ENCOUNTER_STRUCTURAL_KEYS = ['documents', 'billType', 'provider'] as const;

/** Encounter-level fields rendered by the portal's PatientDetailsSection. */
export const PATIENT_DETAILS_FIELDS = ['firstName', 'lastName', 'dateOfBirth', 'medicalRecordNumber', 'insurer', 'memberId'] as const;

/** Encounter-level fields rendered by the portal's EncounterInfoSection. */
export const ENCOUNTER_INFO_FIELDS = ['dateOfService', 'location', 'isGapCase'] as const;

/**
 * Encounter-level fields the back office may toggle — derived from the two
 * portal-section ownership lists above, so a field can't be added to one
 * without the catalog (and the backend's mirrored validator) knowing about it.
 */
export const ENCOUNTER_CONFIGURABLE_KEYS = [...PATIENT_DETAILS_FIELDS, ...ENCOUNTER_INFO_FIELDS] as const;

interface FieldDisplayMeta {
  label: string;
  group: string;
}

/** Display metadata for the backoffice EncounterFieldsCard table. */
export const ENCOUNTER_FIELD_DISPLAY: Record<string, FieldDisplayMeta> = {
  firstName: { label: 'First Name', group: 'Patient' },
  lastName: { label: 'Last Name', group: 'Patient' },
  dateOfBirth: { label: 'Date of Birth', group: 'Patient' },
  medicalRecordNumber: { label: 'Medical Record Number (MRN)', group: 'Patient' },
  dateOfService: { label: 'Date of Service', group: 'Encounter' },
  documents: { label: 'Documents', group: 'Encounter' },
  billType: { label: 'Bill Type', group: 'Encounter' },
  provider: { label: 'Provider', group: 'Encounter' },
  insurer: { label: 'Insurer', group: 'Insurance' },
  memberId: { label: 'Member ID', group: 'Insurance' },
  location: { label: 'Location', group: 'Encounter' },
  isGapCase: { label: 'Gap Case', group: 'Encounter' },
};

// `cpt` covers CPT codes AND their modifiers as one setting (feature 043 refinement) —
// modifiers are part of the CPT input, not a separately-toggled field.
const CODING_FIELDS_ALL_TYPES = ['icd10', 'cpt'] as const;
// Feature 044 — the anesthesia window is four independently-toggled fields (start date,
// start time, end date, end time). It replaced a start time / end time / cross-day-end-date
// triple, so `anesthesiaEndDate` is redefined rather than added: it now toggles the end date
// field itself instead of the retired "Cross day" checkbox.
const ANESTHESIA_ONLY_FIELDS = [
  'asaClassification',
  'anesthesiaStartDate',
  'anesthesiaStartTime',
  'anesthesiaEndDate',
  'anesthesiaEndTime',
  'asaCodes',
] as const;

/**
 * Claim-level billing fields a practice may mark **required**. Every other claim-level
 * field is visible-only (feature 043 refinement): CPT (+modifiers), the anesthesia
 * time fields, and ASA codes are never required — only a diagnosis (ICD-10) and the
 * ASA classification can be. Enforced on resolve (below) and by the backend catalog.
 */
export const CLAIM_LEVEL_REQUIREABLE_FIELDS: ReadonlySet<string> = new Set(['icd10', 'asaClassification']);

/**
 * Claim-level billing fields configurable per claim type. Keyed by the same claim
 * types selectable at submission (`CLAIM_TYPE_PROVIDER_CONFIG`) — single source of
 * truth for per-claim field applicability (Principle 11).
 */
export const CLAIM_LEVEL_FIELDS_BY_TYPE: Record<string, readonly string[]> = Object.fromEntries(
  Object.keys(CLAIM_TYPE_PROVIDER_CONFIG).map((claimType) => [
    claimType,
    claimType === 'ANESTHESIA' ? [...CODING_FIELDS_ALL_TYPES, ...ANESTHESIA_ONLY_FIELDS] : CODING_FIELDS_ALL_TYPES,
  ]),
);

/** Display metadata for the backoffice ClaimFieldsCard matrix. */
export const CLAIM_FIELD_DISPLAY: Record<string, FieldDisplayMeta> = {
  icd10: { label: 'ICD-10', group: 'Coding' },
  cpt: { label: 'CPT & Modifiers', group: 'Coding' },
  asaClassification: { label: 'ASA Classification', group: 'Anesthesia' },
  anesthesiaStartDate: { label: 'Anesthesia Start Date', group: 'Anesthesia' },
  anesthesiaStartTime: { label: 'Anesthesia Start Time', group: 'Anesthesia' },
  anesthesiaEndDate: { label: 'Anesthesia End Date', group: 'Anesthesia' },
  anesthesiaEndTime: { label: 'Anesthesia End Time', group: 'Anesthesia' },
  asaCodes: { label: 'ASA Codes', group: 'Anesthesia' },
};

const VISIBLE_REQUIRED: FormFieldSetting = { visible: true, required: true };
const VISIBLE_OPTIONAL: FormFieldSetting = { visible: true, required: false };

/**
 * Per-field default setting for the encounter-level catalog (structural + configurable
 * keys). Every field defaults to visible+required except the ones listed here — MRN is
 * the first exception (it has always been optional wherever it existed in the app).
 */
const ENCOUNTER_FIELD_DEFAULT: Record<string, FormFieldSetting> = {
  medicalRecordNumber: VISIBLE_OPTIONAL,
};

/** Per-field default setting for each claim-level billing key (today's form behavior). */
export const CLAIM_LEVEL_FIELD_DEFAULT: Record<string, FormFieldSetting> = {
  icd10: VISIBLE_OPTIONAL,
  cpt: VISIBLE_OPTIONAL,
  asaClassification: VISIBLE_REQUIRED,
  anesthesiaStartDate: VISIBLE_OPTIONAL,
  anesthesiaStartTime: VISIBLE_OPTIONAL,
  anesthesiaEndDate: VISIBLE_OPTIONAL,
  anesthesiaEndTime: VISIBLE_OPTIONAL,
  asaCodes: VISIBLE_OPTIONAL,
};

/**
 * Canonical default config mirroring today's form (zero regression for an
 * unconfigured practice, SC-002). Fully populated — every catalog key, and every
 * claim-level field for every catalog claim type — so `resolveEncounterFieldsConfig`
 * can deep-merge a partial/empty saved config over a complete baseline.
 */
export const DEFAULT_ENCOUNTER_FIELDS_CONFIG: EncounterFieldsConfig = {
  encounterFields: Object.fromEntries(
    [...ENCOUNTER_STRUCTURAL_KEYS, ...ENCOUNTER_CONFIGURABLE_KEYS].map(
      (key) => [key, ENCOUNTER_FIELD_DEFAULT[key] ?? VISIBLE_REQUIRED],
    ),
  ),
  claimTypeFields: Object.fromEntries(
    Object.entries(CLAIM_LEVEL_FIELDS_BY_TYPE).map(([claimType, fields]) => [
      claimType,
      Object.fromEntries(fields.map((field) => [field, CLAIM_LEVEL_FIELD_DEFAULT[field]])),
    ]),
  ),
};

/** A hidden field is never required, regardless of what was stored. */
function enforceInvariant(setting: FormFieldSetting): FormFieldSetting {
  return !setting.visible && setting.required ? { visible: false, required: false } : setting;
}

/**
 * Claim-level invariants: the base hidden⇒not-required rule, plus non-requireable
 * fields (everything outside {@link CLAIM_LEVEL_REQUIREABLE_FIELDS}) can never be
 * required — so a stale/hostile stored `required: true` on e.g. `cpt` is normalized away.
 */
function enforceClaimInvariant(field: string, setting: FormFieldSetting): FormFieldSetting {
  const base = enforceInvariant(setting);
  if (base.required && !CLAIM_LEVEL_REQUIREABLE_FIELDS.has(field)) {
    return { visible: base.visible, required: false };
  }
  return base;
}

/**
 * Deep-merges a saved (possibly partial/empty/hostile) config over
 * {@link DEFAULT_ENCOUNTER_FIELDS_CONFIG}. Both structural and patient/encounter keys
 * resolve field-by-field with the hidden⇒not-required invariant enforced; the
 * cross-field identity-anchor rule is not applied here — it is a save-time guardrail
 * ({@link hasRequiredIdentityAnchor}). Unknown keys in the saved payload are dropped —
 * only catalog keys survive. Every claim type in the catalog is present in the result,
 * not just the ones present in `saved`.
 */
export function resolveEncounterFieldsConfig(
  saved?: Partial<EncounterFieldsConfig> | null,
): EncounterFieldsConfig {
  const savedEncounterFields = saved?.encounterFields ?? {};
  const savedClaimTypeFields = saved?.claimTypeFields ?? {};

  const encounterFields: Record<string, FormFieldSetting> = {};
  for (const key of [...ENCOUNTER_STRUCTURAL_KEYS, ...ENCOUNTER_CONFIGURABLE_KEYS]) {
    encounterFields[key] = enforceInvariant(savedEncounterFields[key] ?? DEFAULT_ENCOUNTER_FIELDS_CONFIG.encounterFields[key]);
  }

  const claimTypeFields: Record<string, Record<string, FormFieldSetting>> = {};
  for (const [claimType, defaults] of Object.entries(DEFAULT_ENCOUNTER_FIELDS_CONFIG.claimTypeFields)) {
    const savedForType = savedClaimTypeFields[claimType] ?? {};
    claimTypeFields[claimType] = Object.fromEntries(
      Object.entries(defaults).map(([field, defaultSetting]) => [
        field,
        enforceClaimInvariant(field, savedForType[field] ?? defaultSetting),
      ]),
    );
  }

  return cascadeStructuralVisibility({ encounterFields, claimTypeFields });
}

/**
 * Bill type governs the per-claim world: if the practice doesn't collect a bill type, no claim is
 * ever created, so providers and every claim-level billing/coding field are moot. Cascade them all
 * to hidden + not-required whenever `billType` is not visible, keeping the portal submit schema, the
 * portal render, and the backoffice editor consistent (feature 043, 2026-07-16 amendment). Applied
 * by {@link resolveEncounterFieldsConfig} and re-applied by the settings editor on every change.
 */
export function cascadeStructuralVisibility(config: EncounterFieldsConfig): EncounterFieldsConfig {
  if (config.encounterFields.billType?.visible !== false) return config;
  const off: FormFieldSetting = { visible: false, required: false };
  return {
    encounterFields: { ...config.encounterFields, provider: off },
    claimTypeFields: Object.fromEntries(
      Object.entries(config.claimTypeFields).map(([type, fields]) => [
        type,
        Object.fromEntries(Object.keys(fields).map((field) => [field, off])),
      ]),
    ),
  };
}

/**
 * Identity-anchor groups (feature 043, 2026-07-16 amendment, widened 2026-07-20): a
 * practice's form must always identify the patient by at least one fully-required
 * group. A group is satisfied only when every key in it is required; the overall
 * guardrail ({@link hasRequiredIdentityAnchor}) passes if ANY group is satisfied. Add
 * a field to a group, or a new group entirely, by editing this array only. Mirrors
 * `IdentityAnchorGroups` in the backend catalog (`FormFieldCatalog.cs`).
 */
export const IDENTITY_ANCHOR_GROUPS: readonly (readonly string[])[] = [
  ['documents'],
  ['firstName', 'lastName', 'dateOfService', 'dateOfBirth'],
];

/**
 * Settings-save guardrail. Enforced on save only (backoffice page + backend catalog);
 * an absent key defaults to required, matching the zero-config baseline.
 */
export function hasRequiredIdentityAnchor(encounterFields: Record<string, FormFieldSetting>): boolean {
  const required = (key: string) => encounterFields[key]?.required ?? true;
  return IDENTITY_ANCHOR_GROUPS.some((group) => group.every(required));
}
